import React, { createContext, useState, useEffect, useCallback, useContext } from 'react'
import { notification as antNotification } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { getSSEService } from '@/services/sse.service'
import { useAuth } from '@/hooks/useAuth'
import { API_BASE_URL } from '@/config'

export interface Notification {
  id: string
  userId: string | null
  type: 'approval_request' | 'permission_granted' | 'permission_revoked' | 'admin_alert' | 'system'
  severity: 'info' | 'warning' | 'critical'
  title: string
  message: string
  data?: Record<string, unknown>
  isRead: boolean
  createdAt: string
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  isConnected: boolean
  markAsRead: (notificationId: string) => void
  markAllAsRead: () => void
  clearNotifications: () => void
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)

  // Calculate unread count whenever notifications change
  useEffect(() => {
    const count = notifications.filter((n) => !n.isRead).length
    setUnreadCount(count)
  }, [notifications])

  // Handle SSE connection when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setIsConnected(false)
      return
    }

    const token = localStorage.getItem('auth_token')
    if (!token) {
      return
    }

    const sseService = getSSEService(API_BASE_URL)

    // Connect to SSE endpoint
    sseService.connect(token, (message) => {
      console.log('[NotificationProvider] SSE message:', message)

      if (message.type === 'connected') {
        setIsConnected(true)
        console.log('[NotificationProvider] SSE connected')
      } else if (message.type === 'notification' && message.data) {
        const newNotification = message.data as Notification

        // Add to local state
        setNotifications((prev) => [newNotification, ...prev])

        // Invalidate approval-related queries when notifications arrive
        if (newNotification.type === 'approval_request') {
          // New approval request - refresh approval queue
          console.log(
            '[NotificationProvider] Approval request notification - refreshing approval queue'
          )
          queryClient.invalidateQueries({ queryKey: ['permission-requests'] })
        } else if (
          newNotification.type === 'permission_granted' ||
          newNotification.type === 'permission_revoked'
        ) {
          // Approval decision made - refresh both queue and history
          console.log(
            '[NotificationProvider] Permission decision notification - refreshing approval views'
          )
          queryClient.invalidateQueries({ queryKey: ['permission-requests'] })
          queryClient.invalidateQueries({ queryKey: ['permission-requests-history'] })
        }

        // Show toast notification based on severity
        if (newNotification.severity === 'critical') {
          antNotification.error({
            message: newNotification.title,
            description: newNotification.message,
            duration: 0, // Don't auto-close critical notifications
            placement: 'topRight',
          })
        } else if (newNotification.severity === 'warning') {
          antNotification.warning({
            message: newNotification.title,
            description: newNotification.message,
            duration: 8,
            placement: 'topRight',
          })
        } else {
          antNotification.info({
            message: newNotification.title,
            description: newNotification.message,
            duration: 5,
            placement: 'topRight',
          })
        }
      }
    })

    // Handle connection errors
    sseService.onError((error) => {
      console.error('[NotificationProvider] SSE error:', error)
      setIsConnected(false)
    })

    // Cleanup on unmount or when auth changes
    return () => {
      console.log('[NotificationProvider] Disconnecting SSE')
      sseService.disconnect()
      setIsConnected(false)
    }
  }, [isAuthenticated, queryClient])

  const markAsRead = useCallback((notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    )
  }, [])

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
  }, [])

  const clearNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearNotifications,
  }

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

// Custom hook to use notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
